import { applyRelationalOperator } from '@/utils/relationalFunctions';
import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition.ts';
import {
  FilterRowSelection,
  FilterRowSelectionValue,
  FilterSelections,
  RelationalOperator,
} from '@lambda-layer/types/filter.ts';
import { DatabaseMetadata } from '@lambda-layer/models/FilterDefinition/FilterDefinitionFields.ts';
import { CountMapUtils } from '@/utils/CountMapUtils';
import log from 'loglevel';

/**
 * Class responsible for calculating filter counts based on the selected filters.
 * Note: Exclusions are handled at the repository level, so no client-side exclusion processing is needed.
 */
export class FilterCountCalculator {
  private readonly filterDefinitions: FilterDefinition[];
  private fieldSelectionCountMap: Record<string, Map<string | null, number>>;
  private readonly filterSelections: FilterSelections;

  /**
   * Creates an instance of FilterCountCalculator.
   *
   * @param filterDefinitions - An array of filter fields to be considered.
   * @param fieldSelectionCountMap - A record containing the counts for each filter field.
   * @param filterSelections - The current selections made for each filter field.
   */
  constructor(
    filterDefinitions: FilterDefinition[],
    fieldSelectionCountMap: Record<string, Map<string | null, number>>,
    filterSelections: FilterSelections
  ) {
    this.filterDefinitions = filterDefinitions.filter(
      f =>
        f.controlType &&
        f.databaseMetadata &&
        f.csvMetadata.fieldType !== 'limit'
    );
    this.fieldSelectionCountMap = fieldSelectionCountMap;
    this.filterSelections = filterSelections;
  }

  // -------------------------
  // Helpers for "ALL" handling
  // -------------------------

  /** Normalize any value for comparison; null/undefined -> '', others -> String() */
  private static norm(v: unknown): string {
    return v == null ? '' : String(v);
  }

  /** Whether a selection includes the literal "ALL" sentinel */
  private static hasAll(selection?: FilterRowSelection): boolean {
    const vals = selection?.value ?? [];
    return vals.some(v => FilterCountCalculator.norm(v) === 'ALL');
  }

  /** Remove "ALL" sentinel from a selection's values */
  private static stripAllValues<T = unknown>(
    selection?: FilterRowSelection
  ): T[] {
    const vals = (selection?.value ?? []) as T[];
    return vals.filter(v => FilterCountCalculator.norm(v) !== 'ALL');
  }

  /**
   * Calculates the counts for all filter fields based on the current selections.
   *
   * @returns A record where the key is the filter field name and the value is the count.
   */
  public calculateAllFilterCounts(): Record<string, number> {
    // Calculate the initial count based on the primary field selections
    let lastCount = 0;
    const resultCounts: Record<string, number> = {};

    // Iterate through each filter field to calculate counts
    this.filterDefinitions.forEach(filterDefinition => {
      const dataType = filterDefinition.databaseMetadata?.dataType || 'string';
      const selections = this.filterSelections[filterDefinition.id];

      // Treat "ALL" as no selection (omit the filter)
      const cleanedValues =
        FilterCountCalculator.stripAllValues<FilterRowSelectionValue>(
          selections
        );
      const hasEffectiveSelection = cleanedValues.length > 0;

      if (!hasEffectiveSelection) {
        // No effective selection for this field => do not change the running count
        resultCounts[filterDefinition.id] = lastCount;
      } else {
        const selectionCount = this.fieldSelectionCountMap[filterDefinition.id];
        if (!selectionCount) {
          throw new Error(
            `No selection count found for filter field ${filterDefinition.id}`
          );
        }

        // For array fields, use the total matching record count directly
        if (filterDefinition.isArrayField) {
          lastCount = CountMapUtils.getTotalCount(selectionCount);
        } else if (filterDefinition.isAggregateField) {
          // Sanitize the selection passed to matching (no "ALL")
          const sanitizedSelection: FilterRowSelection = {
            ...selections!,
            value: cleanedValues,
          };

          const matchingValues = this.getMatchingValues(
            dataType,
            sanitizedSelection,
            selectionCount
          );

          lastCount = this.sumCounts(
            filterDefinition.id,
            matchingValues.map(v => v?.toString() || null).values()
          );
        } else {
          lastCount = CountMapUtils.getTotalCount(selectionCount);
        }
        resultCounts[filterDefinition.id] = lastCount;
      }
    });

    return resultCounts;
  }

  /**
   * Get a subset of values from all potential values that match the user input value and relational operator
   *
   * @param dataType
   * @param userValues
   * @param valueCounts
   * @private
   */
  private getMatchingValues(
    dataType: DatabaseMetadata['dataType'],
    userValues: FilterRowSelection,
    valueCounts: Map<string | null, number>
  ) {
    // If "ALL" is present OR no explicit values selected, return all available values
    const containsAll = FilterCountCalculator.hasAll(userValues);
    const noValues = (userValues.value?.length ?? 0) === 0;

    // Exclude the aggregate 'count' key from counting
    const potentialValues = CountMapUtils.filterOutTotalCount(
      Array.from(valueCounts.entries())
    ).map(([key]) => key);

    if ((containsAll || noValues) && potentialValues.length > 0) {
      // Return all available values from the count map (already excludes the total key)
      return potentialValues;
    }

    // Apply relational operator logic for number, string, or boolean types when the operator is not EQUAL.
    if (
      (dataType === 'number' ||
        dataType === 'string' ||
        dataType === 'boolean') &&
      userValues.relationalOperator &&
      userValues.relationalOperator !== RelationalOperator.EQUAL
    ) {
      // Ensure we never pass "ALL" into relational logic
      const sanitized: FilterRowSelection = {
        ...userValues,
        value: FilterCountCalculator.stripAllValues(userValues),
      };
      return applyRelationalOperator(
        sanitized,
        potentialValues.values(),
        dataType
      );
    }

    // Default: use the user's (sanitized) value list
    return FilterCountCalculator.stripAllValues(userValues);
  }

  /**
   * Sums the counts for the specified field and values.
   *
   * @param fieldId - The identifier of the filter field.
   * @param values - The values for which counts are to be summed.
   * @returns The total count.
   */
  private sumCounts(
    fieldId: string,
    values: IterableIterator<string | null>
  ): number {
    const filterDefinition = this.filterDefinitions.find(
      fd => fd.id === fieldId
    );

    if (!filterDefinition?.databaseMetadata?.columnName) {
      log.warn(`No database metadata found for field ${fieldId}`);
      return 0;
    }

    // Sum counts for the specified values
    return Array.from(values).reduce((acc, item) => {
      return acc + (this.fieldSelectionCountMap[fieldId].get(item) || 0);
    }, 0);
  }
}
